<template>
  <div :class="$style.hero">
    <div :class="[$style.logoRow, 'layout-container']">
      <img
        :src="howLogo"
        alt=""
        :class="$style.logo"
        data-test="how-logo"
        width="144"
        height="56"
      />
    </div>
    <div :class="[$style.container, 'layout-container']">
      <div :class="$style.content">
        <h1 :class="$style.title">{{ t('myProperties.landing.hero.title') }}</h1>

        <ul :class="[$style.features, $style.newDesign]">
          <li
            v-for="(feature, index) in features"
            :key="index"
            :class="[$style.featureItem, { [$style.is24]: !isHg }]"
            data-test="feature-item"
          >
            <HgIcon icon="Check" size="small" :class="$style.checkIcon" />
            <span>{{ feature.text }}</span>
          </li>
        </ul>

        <div :class="$style.ctaWrapper">
          <div :class="$style.ctaGrid">
            <HgButton
              :class="$style.ctaPropertyType"
              size="large"
              :theme="isHg ? 'secondary' : 'primary'"
              data-test="cta-house"
              @click="goToPriceCalculatorWithType(PropertyType.House)"
            >
              <span :class="$style.ctaContent">
                <img :src="houseIcon" alt="" :class="$style.icon" />
                {{ t('common.house') }}
              </span>
            </HgButton>
            <HgButton
              :class="$style.ctaPropertyType"
              size="large"
              :theme="isHg ? 'secondary' : 'primary'"
              data-test="cta-apartment"
              @click="goToPriceCalculatorWithType(PropertyType.Apartment)"
            >
              <span :class="$style.ctaContent">
                <img :src="apartmentIcon" alt="" :class="$style.icon" />
                {{ t('common.apartment') }}
              </span>
            </HgButton>
          </div>
          <p :class="$style.footerText">
            {{ t('myProperties.landing.hero.ctaHelper') }}
          </p>
        </div>
      </div>

      <div :class="$style.imageWrapper">
        <img
          :src="heroBackgroundImage"
          alt=""
          :class="$style.heroBackgroundImage"
          data-test="hero-background-image"
          width="1500"
          height="1077"
          loading="eager"
          decoding="async"
        />
        <img
          :src="heroImage"
          :alt="t('myProperties.landing.hero.heroImageAlt')"
          :class="$style.heroImage"
          data-test="hero-image"
          width="1500"
          height="1077"
          loading="eager"
          decoding="async"
        />
        <img
          v-if="overlayHeroImage"
          :src="overlayHeroImage"
          alt=""
          aria-hidden="true"
          :class="[$style.heroImage, $style.overlayHeroImage]"
          data-test="hero-image-overlay"
          width="1500"
          height="1077"
          loading="eager"
          decoding="async"
        />
      </div>
    </div>

    <!-- Why homeowners section -->
    <section :class="$style.whySection">
      <div class="layout-container">
        <div
          :class="[$style.whyCard, $style.newDesign, { [$style.is24]: !isHg }]"
          data-test="why-card"
        >
          <h2 :class="$style.whyTitle">{{ t('myProperties.landing.hero.why.title') }}</h2>
          <div :class="$style.whyGrid">
            <div
              v-for="(item, index) in whyItems"
              :key="index"
              :class="[$style.whyItem, $style.newDesign, { [$style.is24]: !isHg }]"
            >
              <div
                :class="$style.whyIcon"
                :style="
                  !isHg && item.background ? { backgroundImage: `url(${item.background})` } : {}
                "
              >
                <HgIcon :icon="item.icon" size="small" />
              </div>
              <span>{{ item.text }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useRotatingImages } from '@/composables/useRotatingImages';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';

import { isPlatformHg } from '@/helpers/Platform/Platform';
import { getPropertyLandingImageUrl } from '@/helpers/Url/Url';
import { VALUATION_FUNNEL_PATH } from '@/router/routes/constants';
import { TRACKING_ACTION, TRACKING_CATEGORY } from '@/tracking/types';
import { HgButton, HgIcon } from '@smg-real-estate/domus';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import iconApartmentHg from '@/assets/icons/apartment-hg-small.svg?url';
import iconApartmentIs24 from '@/assets/icons/apartment-is24-small.svg?url';
import highlighterTriangle from '@/assets/icons/attitudehighlighter-triangle-is24.svg?assetResource';
import highlighterCheckCross from '@/assets/icons/highlighters-check-cross-is24.svg?assetResource';
import highlighterRect from '@/assets/icons/highlighters-rec-square-is24.svg?assetResource';
import iconHouseHg from '@/assets/icons/house-hg-small.svg?url';
import iconHouseIs24 from '@/assets/icons/house-is24-small.svg?url';
import { PropertyType } from '@/types';

interface Feature {
  icon: any;
  text: string;
  background?: string;
}

const isHg = isPlatformHg();
const houseIcon = computed(() => (isHg ? iconHouseHg : iconHouseIs24));
const apartmentIcon = computed(() => (isHg ? iconApartmentHg : iconApartmentIs24));
const howLogo = ref('');
const { locale, t } = useI18n();
const router = useRouter();
const store = useStore();
const { trackCtaClickWithTreatment } = useTrackingWithTreatment();
const heroPlatformSuffix = isHg ? 'hg' : 'is24';

async function loadHowLogo(): Promise<void> {
  const image = await import(
    `@/assets/images/landing-hero/how-logo-${heroPlatformSuffix}-${locale.value}.png?url`
  );

  howLogo.value = image.default;
}

watch(locale, loadHowLogo, { immediate: true });

const heroImageSrcs = computed(() => [
  getPropertyLandingImageUrl(`hero/hero_1_${heroPlatformSuffix}_${locale.value}.png`),
  getPropertyLandingImageUrl(`hero/hero_2_${heroPlatformSuffix}_${locale.value}_v2.png`),
  getPropertyLandingImageUrl(`hero/hero_3_${heroPlatformSuffix}_${locale.value}.png`),
  getPropertyLandingImageUrl(`hero/hero_4_${heroPlatformSuffix}_${locale.value}.png`),
]);

const heroBackgroundImageUrl = getPropertyLandingImageUrl('hero/hero_background_v2.png');
const {
  currentImageSrc: heroImage,
  overlayImageSrc: overlayHeroImage,
  backgroundImageSrc: heroBackgroundImage,
} = useRotatingImages({
  imageSrcs: heroImageSrcs,
  backgroundImageSrc: heroBackgroundImageUrl,
});

const features = computed<Feature[]>(() => {
  if (isHg) {
    return [
      { icon: 'Check', text: t('myProperties.landing.hero.features.free') },
      { icon: 'Check', text: t('myProperties.landing.hero.features.track') },
      { icon: 'Check', text: t('myProperties.landing.hero.features.estimate') },
    ];
  }

  return [
    { icon: 'Check', text: t('myProperties.landing.hero.features.newFree') },
    { icon: 'Check', text: t('myProperties.landing.hero.features.newMarketData') },
    { icon: 'Check', text: t('myProperties.landing.hero.features.newValuations') },
  ];
});

const whyItems = computed<Feature[]>(() => [
  {
    icon: 'TrendingUp',
    text: t('myProperties.landing.hero.why.items.understand'),
    background: highlighterRect,
  },
  {
    icon: 'Lightbulb',
    text: t('myProperties.landing.hero.why.items.clarity'),
    background: highlighterTriangle,
  },
  {
    icon: 'Check',
    text: t('myProperties.landing.hero.why.items.decisions'),
    background: highlighterCheckCross,
  },
]);

const goToPriceCalculatorWithType = (propertyType: PropertyType) => {
  trackCtaClickWithTreatment({
    h_event_category_006: TRACKING_CATEGORY.CTA_CLICK,
    h_event_action_007: TRACKING_ACTION.SELECT_PROPERTY_TYPE,
    h_event_label_008: propertyType,
  });

  store.dispatch('propertyValuation/initData', propertyType);
  router.push(`${VALUATION_FUNNEL_PATH[locale.value]}?pt=${propertyType}`);
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.hero {
  padding: $space-400 0;
  background-color: $color-white;
}

.logoRow {
  margin-bottom: $space-100;
}

.logo {
  display: block;
  width: 144px;
  height: 56px;
  object-fit: contain;
}

.container {
  display: flex;
  flex-direction: column;
  gap: $space-300;

  @include grid-media-query(md) {
    flex-direction: row;
    align-items: flex-start;
  }
}

.content {
  flex: 1;
  display: flex;
  flex-direction: column;

  @include grid-media-query(md) {
    align-self: stretch;
    padding-bottom: $space-100;
  }
}

.title {
  @include heading-1;
  line-height: 1.3;
  margin-top: 0;
  margin-bottom: $space-100;
  color: $color-black;
  text-wrap: balance;

  @include grid-media-query(md) {
    margin-bottom: 0;
  }
}

.features {
  list-style: none;
  padding: 0;
  margin: 0 0 $space-200 0;
  display: flex;
  flex-direction: column;
  gap: $space-50;

  &.newDesign {
    gap: $space-100;

    @include grid-media-query(md) {
      gap: $space-150;
    }
  }

  @include grid-media-query(md) {
    flex: 1;
    justify-content: center;
    margin-bottom: 0;
  }
}

.featureItem {
  display: flex;
  align-items: center;
  gap: $space-100;
  @include body-2;
}

.checkIcon {
  flex-shrink: 0;
}

.ctaWrapper {
  display: flex;
  flex-direction: column;
  gap: $space-50;
  width: fit-content;
}

.ctaGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $space-100;
}

.ctaPropertyType {
  width: 100%;
  margin: 0;
  color: $color-blue-60;
  padding-block: $space-50 !important;
}

.ctaContent {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: $space-50;
}

.icon {
  display: inline-block;
  flex-shrink: 0;
}

.footerText {
  @include body-3;
  color: gray;
  margin: 0;
}

.imageWrapper {
  flex: 1;
  width: 100%;
  min-width: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1500 / 1077;
}

.heroBackgroundImage {
  position: absolute;
  top: 0;
  bottom: 10px;
  left: 0;
  right: 0;
  width: 100%;
  height: calc(100% - 10px);
  object-fit: cover;
  border-radius: $border-radius-l;
  z-index: 0;
}

.heroImage {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  height: auto;
  border-radius: $border-radius-l;
  z-index: 1;
  filter: drop-shadow(0 $space-25 $space-50 $elevation-shadow-color);
}

.overlayHeroImage {
  opacity: 1;
  animation: fadeOut 0.2s ease-in forwards;
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

.whySection {
  margin-top: $space-400;
  padding: 0;
}

.whyCard {
  background-color: $color-magenta-0;
  padding: $space-100;
  border-radius: 28px;

  &.is24 {
    background-color: $color-teal-50;
  }

  &.newDesign {
    background-color: transparent;
  }
}

.whyTitle {
  @include heading-4;
  margin-top: 0;
  margin-left: $space-50;
}

.whyGrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: $space-50;

  @include grid-media-query(md) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.whyItem {
  display: flex;
  align-items: center;
  gap: $space-50;
  background-color: $color-white;
  padding: $space-50;
  border-radius: 100px;
  box-shadow: $elevation-minimal;
  @include body-3-bold;

  .whyIcon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background-color: $color-blue-0;
    border-radius: 50%;
    color: $color-blue-60;
  }

  &.is24 {
    .whyIcon {
      background-color: $color-teal-50;
      color: $color-white;
    }
  }

  &.newDesign {
    .whyIcon {
      width: 40px;
      height: 40px;
    }

    &.is24 .whyIcon {
      background-color: transparent;
      background-image: none;
      background-size: 50%;
      background-repeat: no-repeat;
      background-position: 65% 40%;
    }
  }
}
</style>
